import { useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import {
  setup,
  initConnection,
  endConnection,
  getSubscriptions,
  getProducts,
  requestSubscription,
  requestPurchase,
  getAvailablePurchases,
  getPendingPurchasesIOS,
  finishTransaction,
  purchaseUpdatedListener,
  purchaseErrorListener,
  ReplacementModesAndroid,
} from 'react-native-iap';
import { getPaidBasePlan, getSubscriptionPrice } from '@/constants/utils/subscriptionPricing';
import { nativeValidationResult, googleReplacementPurchase, IOS_PREMIUM_PRODUCT_IDS, CREDIT_PACK_CREDITS, getCreditPackIapProductId } from '@/lib/nativePayment';
import { PROCESSOR } from '@/lib/purchaseUtils';
import { API_HOST } from '@/constants/config';
import { useAuthStore } from '@/store';
import { trackWhopPurchase } from '@/constants/utils/analytics';
export { getCreditPackIapProductId } from '@/lib/nativePayment';

const isApplePlatform = Platform.OS === 'ios' || Platform.OS === 'macos';
const isNativePlatform = isApplePlatform || Platform.OS === 'android';

// ---------------------------------------------------------------------------
// Sandbox / environment detection
// ---------------------------------------------------------------------------
// The backend uses StoreKit 2 JWS verification and automatically falls back
// between sandbox and production environments. The `sandbox` flag sent by
// the client is an optimisation hint so the backend tries the right
// environment first, but it's no longer critical for correctness.
// ---------------------------------------------------------------------------

/**
 * Detect whether the current build is likely using Apple's IAP sandbox.
 * Sent as a hint to the backend so it tries the correct environment first.
 */
const detectIapSandbox = () => {
  // 1. Explicit env-var override (set in eas.json for preview/staging builds)
  const envOverride = process.env.EXPO_PUBLIC_IAP_SANDBOX;
  if (envOverride === 'true' || envOverride === '1') return true;
  if (envOverride === 'false' || envOverride === '0') return false;

  // 2. Dev builds always use sandbox
  if (__DEV__) return true;

  // 3. Non-production API hosts are almost certainly test/staging → sandbox
  const apiUrl = (process.env.EXPO_PUBLIC_API_URL || '').toLowerCase();
  if (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')) return true;
  if (apiUrl.includes('fun.gab.ai')) return true; // staging server

  // 4. APP_VARIANT set by eas.json for dev/preview profiles
  const variant = (process.env.APP_VARIANT || '').toLowerCase();
  if (variant === 'development' || variant === 'preview') return true;

  // Cannot determine — assume production (retry logic will catch TestFlight)
  return false;
};

/** Cached result so we only compute once per app session */
const IS_IAP_SANDBOX = detectIapSandbox();

// Subscription product IDs — must match App Store Connect / Google Play Console / backend
// iOS offers weekly/annual Plus and monthly/annual Plus Premium.
const PRODUCT_IDS = {
  WEEKLY: 'com.anonymous.gabaiapp.plus.weekly',
  YEARLY: 'ai.gab.gabaiapp.plus.yearly',
  ...(Platform.OS === 'ios' ? IOS_PREMIUM_PRODUCT_IDS : {}),
};

const ALL_SUBSCRIPTION_IDS = Object.values(PRODUCT_IDS);

// Credit pack consumable product IDs — must match App Store Connect / Google Play Console / backend
// Only include tiers that exist in the native store catalogs.
const CREDIT_PACK_PRODUCT_IDS = CREDIT_PACK_CREDITS.map((c) => `ai.gab.gabaiapp.credits.${c}`);

// Enable StoreKit 2 at module load time, BEFORE any hooks or listeners run.
// This MUST happen before purchaseUpdatedListener() is called, because the
// listener checks isIosStorekit2() at registration time to decide which
// native module event emitter to subscribe to. If setup() runs later (e.g.
// inside connect()), the listener registers on the SK1 emitter but purchases
// emit from the SK2 emitter, so events never arrive and purchases time out.
if (isApplePlatform) {
  try {
    setup({ storekitMode: 'STOREKIT2_MODE' });
  } catch (e) {
    console.warn('[useIosIap] StoreKit2 setup failed (IAP may be unavailable):', e.message);
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// StoreKit 2 returns the transaction completed by this purchase sheet. A
// listener can deliver an older unfinished transaction for the same SKU first.
const requestedApplePurchase = (result, productId) => (
  (Array.isArray(result) ? result : [result]).find(purchase => (
    purchase?.productId === productId && purchase.transactionId
  )) || null
);

const isUserCancelledPurchaseError = (err) => {
  const code = err?.code;
  const domain = String(err?.domain || '');
  const message = String(err?.message || '');

  return (
    code === 'E_USER_CANCELLED' ||
    code === 2 ||
    code === '2' ||
    /paymentcancelled/i.test(String(code || '')) ||
    (domain === 'SKErrorDomain' && (code === 2 || code === '2')) ||
    /SKErrorDomain\s*error\s*2/i.test(message)
  );
};

/**
 * Hook for managing iOS/Android In-App Purchases via react-native-iap
 * @returns {Object} IAP state and functions
 */
export default function useIosIap() {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [creditPackProducts, setCreditPackProducts] = useState([]);
  const [error, setError] = useState(null);
  const [purchaseInProgress, updatePurchaseInProgress] = useState(false);
  const [purchasePending, setPurchasePending] = useState(false);
  const pendingTransactions = useRef(new Map());
  const markPurchasePending = useCallback((purchase, pending) => {
    const key = purchase.transactionId || `unconfirmed:${purchase.productId}`;
    if (pending) pendingTransactions.current.set(key, purchase.productId);
    else {
      pendingTransactions.current.delete(key);
      pendingTransactions.current.delete(`unconfirmed:${purchase.productId}`);
    }
    setPurchasePending(pendingTransactions.current.size > 0);
  }, []);
  const purchaseBusy = useRef(false);
  const setPurchaseInProgress = useCallback(value => {
    purchaseBusy.current = value;
    updatePurchaseInProgress(value);
  }, []);
  
  const purchaseUpdateSubscription = useRef(null);
  const purchaseErrorSubscription = useRef(null);
  const purchaseFailsafeTimer = useRef(null);
  const purchasePromiseRef = useRef(null);
  const fetchProductsPromise = useRef(null); // mutex to prevent concurrent fetches
  const storeConnectedRef = useRef(false);
  const appleAppAccountTokenRef = useRef(null);
  // IMPORTANT: The auth store field is `token`, not `accessToken`.
  // Using the wrong name caused accessToken to always be undefined,
  // which silently broke receipt validation for every purchase.
  const accessToken = useAuthStore(state => state.token);
  const accountId = useAuthStore(state => state.user?.id);
  const fetchUser = useAuthStore(state => state.fetchUser);
  const fetchUserCredits = useAuthStore(state => state.fetchUserCredits);

  useEffect(() => {
    appleAppAccountTokenRef.current = null;
  }, [accessToken]);

  useEffect(() => {
    pendingTransactions.current.clear();
    setPurchasePending(false);
  }, [accountId]);

  const getAppleAppAccountToken = useCallback(async () => {
    if (!isApplePlatform) return null;
    if (appleAppAccountTokenRef.current) return appleAppAccountTokenRef.current;
    if (!accessToken) throw new Error('You must be signed in before purchasing');

    let lastError;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await fetch(`${API_HOST}/json/iap/ios/account-token`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData?.message || 'Unable to prepare Apple purchase');
        }
        const data = await response.json().catch(() => ({}));
        const appAccountToken = data?.appAccountToken;
        if (
          data?.error ||
          typeof appAccountToken !== 'string' ||
          !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(appAccountToken)
        ) {
          throw new Error(data?.message || 'Unable to prepare Apple purchase');
        }
        appleAppAccountTokenRef.current = appAccountToken.toLowerCase();
        return appleAppAccountTokenRef.current;
      } catch (err) {
        lastError = err;
        if (attempt < 2) await sleep(250 * (attempt + 1));
      }
    }
    throw lastError || new Error('Unable to prepare Apple purchase');
  }, [accessToken]);

  const getGoogleAccountId = useCallback(async () => {
    if (!accessToken) throw new Error('You must be signed in before purchasing');
    const response = await fetch(`${API_HOST}/json/iap/android/account-id`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await response.json();
    const accountId = data?.accountId || data?.data?.accountId;
    if (!response.ok || data?.error || typeof accountId !== 'string' || !accountId.trim() || accountId.length > 64) {
      throw new Error(data?.message || 'Unable to prepare Google Play purchase');
    }
    return accountId;
  }, [accessToken]);

  // Validate a StoreKit 2 JWS signed transaction with the backend.
  // The backend verifies the JWS cryptographically using Apple's root CA
  // via @apple/app-store-server-library — no verifyReceipt call involved.
  const validateTransaction = useCallback(async (signedTransaction, productId, transactionId) => {
    if (!accessToken) {
      console.error('[useIosIap] No access token for validation');
      return { success: false, error: 'Not authenticated' };
    }

    const endpoint = isApplePlatform
      ? `${API_HOST}/json/iap/ios/validate`
      : `${API_HOST}/json/iap/android/validate`;

    let result;
    try {
      const sandbox = IS_IAP_SANDBOX;
      console.log('[useIosIap] Validating JWS with backend...', {
        productId,
        transactionId,
        sandbox,
        endpoint,
        jwsLength: signedTransaction?.length,
      });

      const request = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          signedTransaction,
          receiptData: signedTransaction,
          ...(Platform.OS === 'android' ? { purchaseToken: signedTransaction } : {}),
          productId,
          transactionId,
          platform: Platform.OS,
          sandbox,
        }),
      };

      let response, data;
      // Validation is idempotent by store transaction ID. Retry an interrupted
      // request or temporary server failure without opening another purchase.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          response = await fetch(endpoint, request);
          data = await response.json().catch(() => ({
            error: true, message: `HTTP ${response.status} — non-JSON response`,
          }));
          const retryable = response.status >= 500 || response.status === 408 || response.status === 429
            || (response.ok && !data?.error && nativeValidationResult(data, productId).pending);
          if (!retryable || attempt === 2) break;
        } catch (requestErr) {
          if (attempt === 2) throw requestErr;
        }
        await sleep(250 * (attempt + 1));
      }

      console.log('[useIosIap] Validation response:', {
        ok: response.ok,
        status: response.status,
        error: data?.error,
        message: data?.message,
      });

      if (!response.ok || data?.error) {
        console.error('[useIosIap] Validation failed:', data);
        const recoveryRequired = isApplePlatform && response.status === 403
          && /Unbound legacy Apple purchase requires support recovery/i.test(data?.message || '');
        const supportTransactionId = typeof transactionId === 'string' && /^\d{1,32}$/.test(transactionId)
          ? transactionId : null;
        result = { success: false, pending: recoveryRequired || response.status >= 500 || response.status === 408 || response.status === 429,
          recoveryRequired, supportTransactionId: recoveryRequired ? supportTransactionId : undefined,
          error: recoveryRequired
            ? `An earlier Apple purchase needs support recovery. Do not purchase again. Contact ai@gab.com${supportTransactionId ? ` with Apple transaction ${supportTransactionId}` : ' with your Apple receipt'}. After support confirms recovery, use Restore Purchases.`
            : data?.message || 'Validation failed' };
      } else {
        console.log('[useIosIap] Transaction validated successfully');
        result = nativeValidationResult(data, productId);
        if (result.success) {
          try {
            trackWhopPurchase({
              id: transactionId || data?.transactionId || productId,
              value: data?.price ?? data?.purchase?.price,
              currency: data?.currency || 'USD',
            });
          } catch (trackingErr) {
            console.warn('[useIosIap] Purchase tracking failed:', trackingErr?.message);
          }
        }
      }
    } catch (err) {
      console.error('[useIosIap] Validation error:', err);
      result = { success: false, pending: true, error: err.message };
    } finally {
      try {
        if (fetchUser) await fetchUser();
        if (fetchUserCredits) await fetchUserCredits();
      } catch (_refreshErr) {
        console.warn('[useIosIap] Post-validation user refresh failed:', _refreshErr?.message);
      }
    }

    return result;
  }, [accessToken, fetchUser, fetchUserCredits]);

  // Recover unfinished Apple and Google purchases after store/auth hydration.
  // ALL transactions are validated with the backend before finishing so that
  // purchases that were never confirmed (e.g. app crash, network failure) are
  // properly recorded. For subscriptions the S2S webhook is the primary
  // server-side path. For credit packs this is the ONLY recovery mechanism —
  // credit packs that fail validation are left unfinished for retry.
  const processPendingIosTransactions = useCallback(async () => {
    if (!isNativePlatform) return;

    try {
      const pending = isApplePlatform && typeof getPendingPurchasesIOS === 'function'
        ? await getPendingPurchasesIOS()
        : await getAvailablePurchases();

      if (!pending || pending.length === 0) {
        console.log('[useIosIap] No pending iOS transactions');
        return;
      }

      console.log('[useIosIap] Found', pending.length, 'pending iOS transaction(s):', pending.map(p => p.productId));

      for (const purchase of pending) {
        const isCreditPack = purchase.productId?.includes?.('.credits.');
        if (Platform.OS === 'android' && purchase.purchaseStateAndroid === 2) continue;
        const signedTxn = isApplePlatform
          ? (purchase.jwsRepresentationIos || purchase.transactionReceipt)
          : purchase.purchaseToken;

        // Never finish any transaction until our backend confirms it recorded
        // the entitlement. Leaving it pending lets StoreKit redeliver after an
        // auth/network/server failure instead of permanently dropping a paid
        // subscription or credit pack.
        if (!signedTxn || !accessToken) {
          console.warn('[useIosIap] Cannot validate pending transaction (no JWS or auth) — leaving for retry:', purchase.productId);
          continue;
        }
        console.log('[useIosIap] Validating pending transaction:', purchase.productId, 'txnId:', purchase.transactionId);
        try {
          const result = await validateTransaction(signedTxn, purchase.productId, purchase.transactionId);
          if (!result.success) {
            if (result.pending) markPurchasePending(purchase, true);
            console.warn('[useIosIap] Pending transaction validation failed — leaving unfinished for retry:', purchase.productId);
            continue;
          }
        } catch (valErr) {
          console.warn('[useIosIap] Pending transaction validation threw — leaving unfinished for retry:', valErr.message);
          continue;
        }

        markPurchasePending(purchase, false);
        console.log('[useIosIap] Finishing pending transaction:', purchase.productId, 'txnId:', purchase.transactionId);
        try {
          await finishTransaction({ purchase, isConsumable: isCreditPack });
          console.log('[useIosIap] Finished pending transaction:', purchase.productId);
        } catch (finishErr) {
          console.warn('[useIosIap] Failed to finish pending transaction:', finishErr.message);
        }
      }
    } catch (err) {
      console.warn('[useIosIap] processPendingIosTransactions error (non-fatal):', err.message);
    }
  }, [accessToken, validateTransaction, markPurchasePending]);

  // Connect to the store
  const connect = useCallback(async () => {
    if (!isNativePlatform) {
      console.log('[useIosIap] Not iOS/Android/macOS, skipping connection');
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const result = await initConnection();
      console.log('[useIosIap] Connection result:', result);
      
      storeConnectedRef.current = true;
      setIsConnected(true);
      console.log('[useIosIap] Connected to store, result:', result);
      return true;
    } catch (err) {
      console.error('[useIosIap] Connection error:', err);
      setError(err.message || 'Failed to connect to store');
      storeConnectedRef.current = false;
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Disconnect from the store
  const disconnect = useCallback(async () => {
    if (!isNativePlatform) return;
    
    try {
      await endConnection();
      storeConnectedRef.current = false;
      setIsConnected(false);
      console.log('[useIosIap] Disconnected from store');
    } catch (err) {
      console.error('[useIosIap] Disconnect error:', err);
    }
  }, []);

  // Fetch subscription and credit pack products from store.
  // Uses a mutex (fetchProductsPromise ref) to ensure only one fetch runs at a
  // time. Concurrent callers share the same in-flight promise instead of firing
  // a second StoreKit request that would cancel the first ("Previous request was
  // cancelled due to a new request").
  const fetchProducts = useCallback(async () => {
    if (!isNativePlatform || !storeConnectedRef.current) {
      console.log('[useIosIap] Cannot fetch products - not connected');
      return { subscriptions: [], creditPacks: [] };
    }

    // If a fetch is already in progress, piggy-back on that promise
    if (fetchProductsPromise.current) {
      console.log('[useIosIap] fetchProducts already in progress — reusing existing request');
      return fetchProductsPromise.current;
    }

    const doFetch = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[useIosIap] Fetching subscriptions:', ALL_SUBSCRIPTION_IDS);
        console.log('[useIosIap] Fetching credit packs:', CREDIT_PACK_PRODUCT_IDS);

        // Fetch subscriptions and credit packs independently so one failing
        // doesn't prevent the other from loading (e.g. subscriptions not yet
        // configured on Google Play should not block credit pack loading).
        let subResults = [];
        try {
          subResults = await getSubscriptions({ skus: ALL_SUBSCRIPTION_IDS });
        } catch (subErr) {
          console.warn('[useIosIap] getSubscriptions failed (non-fatal):', subErr.message);
        }

        // Small pause avoids back-to-back SKProductsRequest overlap on slower devices.
        await sleep(100);

        let creditResults = [];
        try {
          creditResults = await getProducts({ skus: CREDIT_PACK_PRODUCT_IDS });
        } catch (creditErr) {
          console.warn('[useIosIap] getProducts failed (non-fatal):', creditErr.message);
        }

        // iOS fallback: on some StoreKit/react-native-iap combinations, getSubscriptions
        // can return empty even when products exist. Retry subscriptions via getProducts.
        if (isApplePlatform && (!subResults || subResults.length === 0)) {
          console.warn('[useIosIap] getSubscriptions returned 0 on iOS, retrying via getProducts fallback');
          await sleep(150);
          try {
            const fallbackResults = await getProducts({ skus: ALL_SUBSCRIPTION_IDS });
            subResults = (fallbackResults || []).filter((p) => ALL_SUBSCRIPTION_IDS.includes(p.productId));
          } catch (fallbackErr) {
            console.warn('[useIosIap] Subscription fallback failed:', fallbackErr.message);
          }
        }

        // iOS fallback for consumables as well when StoreKit transiently returns empty.
        if (isApplePlatform && (!creditResults || creditResults.length === 0)) {
          console.warn('[useIosIap] getProducts returned 0 credit packs on iOS, retrying once');
          await sleep(150);
          try {
            creditResults = await getProducts({ skus: CREDIT_PACK_PRODUCT_IDS });
          } catch (retryErr) {
            console.warn('[useIosIap] Credit pack retry failed:', retryErr.message);
          }
        }

        console.log('[useIosIap] Subscriptions returned:', subResults?.length || 0,
          subResults?.map(p => `${p.productId} @ ${p.localizedPrice}`));
        console.log('[useIosIap] Credit packs returned:', creditResults?.length || 0,
          creditResults?.map(p => `${p.productId} @ ${p.localizedPrice}`));

        if (subResults?.length > 0) {
          const sorted = subResults.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
          setProducts(sorted);
          console.log('[useIosIap] Successfully loaded', sorted.length, 'subscription products');
        } else {
          console.warn('[useIosIap] No subscription products returned from store');
          setProducts([]);
        }
        if (creditResults?.length > 0) {
          setCreditPackProducts(creditResults);
          console.log('[useIosIap] Successfully loaded', creditResults.length, 'credit pack products');
        } else {
          console.warn('[useIosIap] No credit pack products returned from store');
          setCreditPackProducts([]);
        }
        return {
          subscriptions: Array.isArray(subResults) ? subResults : [],
          creditPacks: Array.isArray(creditResults) ? creditResults : [],
        };
      } catch (err) {
        console.error('[useIosIap] Fetch products error:', err);
        setError(err.message || 'Failed to fetch products');
        return { subscriptions: [], creditPacks: [] };
      } finally {
        setIsLoading(false);
        fetchProductsPromise.current = null; // clear mutex when done
      }
    };

    fetchProductsPromise.current = doFetch();
    return fetchProductsPromise.current;
  }, []);

  // Purchase a subscription product.
  // User-initiated validation happens here; the listener and
  // processPendingIosTransactions also validate as a recovery fallback.
  const purchaseProduct = useCallback(async (productId, selectedProduct) => {
    console.log('[useIosIap] purchaseProduct called:', {
      productId,
      isConnected,
      purchaseInProgress,
      productsCount: products?.length,
      creditPacksCount: creditPackProducts?.length,
    });

    if (!isNativePlatform || !storeConnectedRef.current) {
      console.error('[useIosIap] Cannot purchase - not connected to store');
      return { success: false, error: 'Not connected to store' };
    }

    if (pendingTransactions.current.size) {
      return { success: false, pending: true, error: 'A purchase is still being verified. Use Restore; do not purchase again.' };
    }
    if (purchaseBusy.current) {
      console.warn('[useIosIap] Purchase already in progress');
      return { success: false, error: 'Purchase already in progress' };
    }

    setPurchaseInProgress(true);
    setError(null);

    let appAccountToken = null;
    if (isApplePlatform) {
      try {
        appAccountToken = await getAppleAppAccountToken();
      } catch (tokenErr) {
        console.error('[useIosIap] Could not provision appAccountToken:', tokenErr);
        setPurchaseInProgress(false);
        setError('Unable to prepare Apple purchase. Please try again.');
        return { success: false, error: tokenErr.message };
      }
    }

    // The listener will resolve this with the raw purchase object (no validation)
    const purchaseDataPromise = new Promise((resolve) => {
      purchasePromiseRef.current = { resolve, productId };
    });

    const failsafeTimer = setTimeout(() => {
      console.warn('[useIosIap] Purchase failsafe timeout — resetting');
      if (purchasePromiseRef.current) {
        purchasePromiseRef.current.resolve(null);
        purchasePromiseRef.current = null;
      }
    }, 120000);
    purchaseFailsafeTimer.current = failsafeTimer;

    let storePurchase = null;
    try {
      console.log('[useIosIap] Calling requestSubscription for:', productId);

      const requestParams = {
        sku: productId,
        andDangerouslyFinishTransactionAutomaticallyIOS: false,
      };
      if (appAccountToken) requestParams.appAccountToken = appAccountToken;

      // Android (Google Play Billing Library 5+) requires subscriptionOffers
      // with the offerToken from the product's subscriptionOfferDetails.
      if (Platform.OS === 'android') {
        requestParams.obfuscatedAccountIdAndroid = await getGoogleAccountId();
        const owned = googleReplacementPurchase(await getAvailablePurchases(), ALL_SUBSCRIPTION_IDS);
        const activeSubscription = useAuthStore.getState().user?.activePurchase;
        if ((!owned && activeSubscription?.processor === PROCESSOR.GOOGLE && activeSubscription?.status === 'active')
          || (owned?.obfuscatedAccountIdAndroid && owned.obfuscatedAccountIdAndroid !== requestParams.obfuscatedAccountIdAndroid)) {
          throw new Error('Your existing subscription could not be matched to this account. Please manage it in Google Play.');
        }
        if (owned?.productId === productId) {
          const validation = await validateTransaction(owned.purchaseToken, owned.productId, owned.transactionId);
          if (validation.success) await finishTransaction({ purchase: owned, isConsumable: false }).catch(() => {});
          clearTimeout(failsafeTimer);
          purchaseFailsafeTimer.current = null;
          purchasePromiseRef.current = null;
          setPurchaseInProgress(false);
          return { ...validation, charged: true, recovered: true, pending: !validation.success && validation.pending !== false };
        }
        if (owned) {
          const previous = await validateTransaction(owned.purchaseToken, owned.productId, owned.transactionId);
          if (!previous.success) {
            clearTimeout(failsafeTimer);
            purchaseFailsafeTimer.current = null;
            purchasePromiseRef.current = null;
            setPurchaseInProgress(false);
            return { ...previous, charged: true, pending: previous.pending !== false, recovered: true };
          }
          requestParams.purchaseTokenAndroid = owned.purchaseToken;
          requestParams.replacementModeAndroid = ReplacementModesAndroid.WITH_TIME_PRORATION;
        }
        const product = selectedProduct?.productId === productId ? selectedProduct
          : products.find((p) => p.productId === productId);
        const offerToken = getPaidBasePlan(product)?.offerToken;
        if (!offerToken) throw new Error('The paid subscription plan is unavailable. Please try again later.');
        if (offerToken) {
          requestParams.subscriptionOffers = [{ sku: productId, offerToken }];
        }
        console.log('[useIosIap] Android subscriptionOffers:', {
          hasProduct: !!product,
          hasOfferToken: !!offerToken,
          offerDetailsCount: product?.subscriptionOfferDetails?.length,
        });
      }

      const requested = await requestSubscription(requestParams);
      if (isApplePlatform) storePurchase = requestedApplePurchase(requested, productId);
      console.log('[useIosIap] requestSubscription resolved');
    } catch (reqErr) {
      clearTimeout(failsafeTimer);
      purchaseFailsafeTimer.current = null;
      purchasePromiseRef.current = null;
      console.error('[useIosIap] Purchase error:', reqErr?.code, reqErr?.message);

      if (isUserCancelledPurchaseError(reqErr)) {
        console.log('[useIosIap] Purchase cancelled by user');
        setError(null);
        setPurchaseInProgress(false);
        return { success: false, cancelled: true, error: null };
      }
      setError(reqErr.message || 'Purchase failed');
      setPurchaseInProgress(false);
      return { success: false, error: reqErr.message };
    }

    // Prefer the exact StoreKit result over recovered events for the same SKU.
    const purchase = storePurchase || await purchaseDataPromise;
    purchasePromiseRef.current = null;
    clearTimeout(failsafeTimer);
    purchaseFailsafeTimer.current = null;

    if (!purchase || purchase._cancelled) {
      setPurchaseInProgress(false);
      if (purchase?._cancelled) {
        setError(null);
        return { success: false, cancelled: true, error: null };
      }
      markPurchasePending({ productId }, true);
      setError('The store has not confirmed the purchase. Use Restore to check before trying another purchase.');
      return { success: false, pending: true, charged: false, error: 'The store has not confirmed the purchase. Use Restore; do not purchase again until its status is known.' };
    }

    if (Platform.OS === 'android' && purchase.purchaseStateAndroid === 2) {
      setPurchaseInProgress(false);
      return { success: false, pending: true, charged: false, error: 'Payment is pending in Google Play. Do not purchase again.' };
    }

    if (purchase._error) {
      setPurchaseInProgress(false);
      setError(purchase._error);
      return { success: false, error: purchase._error };
    }

    // --- Only user-initiated purchases reach here: validate with backend ---
    const signedTxn = isApplePlatform
      ? (purchase.jwsRepresentationIos || purchase.transactionReceipt)
      : purchase.purchaseToken;

    console.log('[useIosIap] === USER-INITIATED VALIDATION (subscription) ===');
    console.log('[useIosIap] productId:', purchase.productId);
    console.log('[useIosIap] transactionId:', purchase.transactionId);
    console.log('[useIosIap] signedTxn?', !!signedTxn);

    // `charged` marks the paths the store already took money on: a purchase
    // object exists, so only our own validation is missing. Every earlier
    // failure return leaves it unset, which is what lets the checkout funnel
    // tell a decline apart from a post-charge validation gap.
    if (!signedTxn) {
      markPurchasePending(purchase, true);
      setPurchaseInProgress(false);
      setError('Purchase data unavailable. Please contact support.');
      return { success: false, pending: true, charged: true, error: 'No signed transaction data' };
    }

    const validation = await validateTransaction(signedTxn, purchase.productId, purchase.transactionId);

    if (validation.success) {
      try {
        await finishTransaction({ purchase, isConsumable: false });
        console.log('[useIosIap] Transaction finished (subscription)');
      } catch (finishErr) {
        console.error('[useIosIap] Error finishing transaction:', finishErr);
      }
      setPurchaseInProgress(false);
      return { success: true, charged: true, productId: purchase.productId };
    }

    console.error('[useIosIap] Validation failed:', validation.error);
    if (validation.pending !== false) markPurchasePending(purchase, true);
    setPurchaseInProgress(false);
    setError(validation.recoveryRequired ? validation.error : validation.pending !== false
      ? 'Your purchase is still being verified. Use Restore; do not purchase again.'
      : validation.error || 'Purchase could not be verified. Please contact support.');
    return { ...validation, success: false, pending: validation.pending !== false, charged: true, error: validation.error || 'Validation failed' };
  }, [isConnected, purchaseInProgress, products, creditPackProducts, validateTransaction, getAppleAppAccountToken, getGoogleAccountId, markPurchasePending, setPurchaseInProgress]);

  // Purchase a credit pack (consumable). credits must be one of CREDIT_PACK_CREDITS.
  // User-initiated validation happens here; the listener and
  // processPendingIosTransactions also validate as a recovery fallback.
  const purchaseCreditPack = useCallback(async (credits) => {
    const productId = getCreditPackIapProductId(credits);
    console.log('[useIosIap] purchaseCreditPack called:', {
      credits,
      productId,
      isConnected,
      purchaseInProgress,
    });

    if (!productId) {
      return { success: false, error: 'Unsupported credit pack for in-app purchase' };
    }
    if (!isNativePlatform || !storeConnectedRef.current) {
      return { success: false, error: 'Not connected to store' };
    }
    if (pendingTransactions.current.size) {
      return { success: false, pending: true, error: 'A purchase is still being verified. Use Restore; do not purchase again.' };
    }
    if (purchaseBusy.current) {
      return { success: false, error: 'Purchase already in progress' };
    }

    setPurchaseInProgress(true);
    setError(null);

    let appAccountToken = null;
    if (isApplePlatform) {
      try {
        appAccountToken = await getAppleAppAccountToken();
      } catch (tokenErr) {
        console.error('[useIosIap] Could not provision appAccountToken for credit purchase:', tokenErr);
        setPurchaseInProgress(false);
        setError('Unable to prepare Apple purchase. Please try again.');
        return { success: false, error: tokenErr.message };
      }
    }

    // Recover an existing purchase without charging again.
    if (isNativePlatform) {
      let foundExisting = false;
      try {
        const pending = isApplePlatform && typeof getPendingPurchasesIOS === 'function'
          ? await getPendingPurchasesIOS()
          : await getAvailablePurchases();

        const stuckTxn = (pending || []).find(p => p.productId === productId);
        if (stuckTxn) {
          foundExisting = true;
          if (Platform.OS === 'android' && stuckTxn.purchaseStateAndroid === 2) {
            setPurchaseInProgress(false);
            return { success: false, pending: true, charged: false, error: 'Payment is pending in Google Play. Do not purchase again.' };
          }
          const stuckSignedTxn = isApplePlatform
            ? (stuckTxn.jwsRepresentationIos || stuckTxn.transactionReceipt)
            : stuckTxn.purchaseToken;
          if (!stuckSignedTxn || !accessToken) {
            throw new Error('Existing Apple transaction is waiting for validation');
          }
          const validation = await validateTransaction(stuckSignedTxn, stuckTxn.productId, stuckTxn.transactionId);
          if (!validation.success) {
            setPurchaseInProgress(false);
            return { ...validation, charged: true, pending: validation.pending !== false, recovered: true };
          }
          console.log('[useIosIap] Validated stuck transaction before clearing:', productId);
          try {
            await finishTransaction({ purchase: stuckTxn, isConsumable: true });
            console.log('[useIosIap] Cleared stuck transaction for', productId);
          } catch (finishErr) {
            console.warn('[useIosIap] Could not finish stuck transaction:', finishErr.message);
          }
          setPurchaseInProgress(false);
          return { success: true, charged: true, recovered: true, productId };
        }
      } catch (pendingErr) {
        console.warn('[useIosIap] Pending transaction check failed; refusing a second purchase:', pendingErr.message);
        setPurchaseInProgress(false);
        setError('A previous purchase is still being verified. Please try Restore.');
        return { success: false, pending: foundExisting, charged: foundExisting, error: pendingErr.message };
      }
    }

    // The listener will resolve this with the raw purchase object (no validation)
    const purchaseDataPromise = new Promise((resolve) => {
      purchasePromiseRef.current = { resolve, productId };
    });

    const failsafeTimer = setTimeout(() => {
      console.warn('[useIosIap] Credit pack purchase failsafe timeout — resetting');
      if (purchasePromiseRef.current) {
        purchasePromiseRef.current.resolve(null);
        purchasePromiseRef.current = null;
      }
    }, 120000);
    purchaseFailsafeTimer.current = failsafeTimer;

    let storePurchase = null;
    try {
      const requestParams = {
        sku: productId,
        andDangerouslyFinishTransactionAutomaticallyIOS: false,
      };
      if (appAccountToken) requestParams.appAccountToken = appAccountToken;

      if (Platform.OS === 'android') {
        requestParams.skus = [productId];
        requestParams.obfuscatedAccountIdAndroid = await getGoogleAccountId();
      }

      console.log('[useIosIap] Calling requestPurchase for credit pack:', productId, Platform.OS === 'android' ? '(with skus array)' : '');
      const requested = await requestPurchase(requestParams);
      if (isApplePlatform) storePurchase = requestedApplePurchase(requested, productId);
      console.log('[useIosIap] requestPurchase resolved');
    } catch (reqErr) {
      clearTimeout(failsafeTimer);
      purchaseFailsafeTimer.current = null;
      purchasePromiseRef.current = null;
      console.error('[useIosIap] Credit pack purchase error:', reqErr?.code, reqErr?.message);

      if (isUserCancelledPurchaseError(reqErr)) {
        console.log('[useIosIap] Credit pack purchase cancelled by user');
        setError(null);
        setPurchaseInProgress(false);
        return { success: false, cancelled: true, error: null };
      }
      setError(reqErr.message || 'Purchase failed');
      setPurchaseInProgress(false);
      return { success: false, error: reqErr.message };
    }

    const purchase = storePurchase || await purchaseDataPromise;
    purchasePromiseRef.current = null;
    clearTimeout(failsafeTimer);
    purchaseFailsafeTimer.current = null;

    if (!purchase || purchase._cancelled) {
      setPurchaseInProgress(false);
      if (purchase?._cancelled) {
        setError(null);
        return { success: false, cancelled: true, error: null };
      }
      markPurchasePending({ productId }, true);
      setError('The store has not confirmed the purchase. Use Restore to check before trying another purchase.');
      return { success: false, pending: true, charged: false, error: 'The store has not confirmed the purchase. Use Restore; do not purchase again until its status is known.' };
    }

    if (Platform.OS === 'android' && purchase.purchaseStateAndroid === 2) {
      setPurchaseInProgress(false);
      return { success: false, pending: true, charged: false, error: 'Payment is pending in Google Play. Do not purchase again.' };
    }

    if (purchase._error) {
      setPurchaseInProgress(false);
      setError(purchase._error);
      return { success: false, error: purchase._error };
    }

    // --- Only user-initiated purchases reach here: validate with backend ---
    const signedTxn = isApplePlatform
      ? (purchase.jwsRepresentationIos || purchase.transactionReceipt)
      : purchase.purchaseToken;

    console.log('[useIosIap] === USER-INITIATED VALIDATION (credit pack) ===');
    console.log('[useIosIap] productId:', purchase.productId);
    console.log('[useIosIap] transactionId:', purchase.transactionId);
    console.log('[useIosIap] signedTxn?', !!signedTxn);

    // See the subscription path above for what `charged` means.
    if (!signedTxn) {
      markPurchasePending(purchase, true);
      setPurchaseInProgress(false);
      setError('Purchase data unavailable. Please contact support.');
      return { success: false, pending: true, charged: true, error: 'No signed transaction data' };
    }

    const validation = await validateTransaction(signedTxn, purchase.productId, purchase.transactionId);

    if (validation.success) {
      try {
        await finishTransaction({ purchase, isConsumable: true });
        console.log('[useIosIap] Transaction finished (consumable)');
      } catch (finishErr) {
        console.error('[useIosIap] Error finishing transaction:', finishErr);
      }
      setPurchaseInProgress(false);
      return { success: true, charged: true, productId: purchase.productId };
    }

    console.error('[useIosIap] Credit pack validation failed:', validation.error);
    if (validation.pending !== false) markPurchasePending(purchase, true);
    setPurchaseInProgress(false);
    setError(validation.recoveryRequired ? validation.error : validation.pending !== false
      ? 'Your purchase is still being verified. Use Restore; do not purchase again.'
      : validation.error || 'Purchase could not be verified. Please contact support.');
    return { ...validation, success: false, pending: validation.pending !== false, charged: true, error: validation.error || 'Validation failed' };
  }, [isConnected, purchaseInProgress, accessToken, validateTransaction, getAppleAppAccountToken, getGoogleAccountId, markPurchasePending, setPurchaseInProgress]);

  // Restore purchases
  const restorePurchases = useCallback(async () => {
    if (!isNativePlatform) {
      return { success: false, error: 'Not connected to store' };
    }

    try {
      setIsLoading(true);
      setError(null);
      if (!storeConnectedRef.current && !await connect()) {
        return { success: false, error: 'Unable to connect to the store. Please try Restore again.' };
      }

      console.log('[useIosIap] Restoring purchases...');
      // onlyIncludeActiveItems: skip expired trials so we do not send the
      // bounced weekly receipt and miss a later paid transaction.
      // Current entitlements do not include every unfinished consumable.
      // Include the pending queue to recover a paid credit pack after
      // interrupted verification without requiring another app launch.
      const lists = await Promise.allSettled([
        getAvailablePurchases({ onlyIncludeActiveItems: true }),
        ...(isApplePlatform && typeof getPendingPurchasesIOS === 'function'
          ? [getPendingPurchasesIOS()] : []),
      ]);
      let lastError = null;
      let pending = false;
      const transactions = new Map();
      for (const list of lists) {
        if (list.status === 'rejected') {
          lastError = list.reason?.message || 'Unable to check purchases. Please try Restore again.';
          pending = true;
          continue;
        }
        for (const purchase of list.value || []) {
          const id = purchase.transactionId || purchase.purchaseToken
            || purchase.jwsRepresentationIos || purchase.transactionReceipt || transactions.size;
          const key = `${purchase.productId}:${id}`;
          transactions.set(key, { ...transactions.get(key), ...purchase });
        }
      }
      const purchases = [...transactions.values()];

      if (purchases.length === 0 && !lastError) {
        for (const key of pendingTransactions.current.keys()) {
          if (key.startsWith('unconfirmed:')) pendingTransactions.current.delete(key);
        }
        setPurchasePending(pendingTransactions.current.size > 0);
        if (pendingTransactions.current.size) return { success: false, pending: true,
          error: 'A purchase is still being verified. Please try Restore again; do not purchase again.' };
        console.log('[useIosIap] No purchases to restore');
        return { success: true, message: 'No purchases to restore' };
      }

      console.log('[useIosIap] Found purchases to restore:', purchases.length);

      let granted = false;
      for (const purchase of purchases) {
        if (Platform.OS === 'android' && purchase.purchaseStateAndroid === 2) {
          lastError = 'Payment is pending in Google Play. Do not purchase again.';
          pending = true;
          continue;
        }
        const signedTxn = isApplePlatform
          ? (purchase.jwsRepresentationIos || purchase.transactionReceipt)
          : purchase.purchaseToken;
        if (!signedTxn) {
          lastError = 'Purchase data is unavailable. Please try Restore again; do not purchase again.';
          pending = true;
          markPurchasePending(purchase, true);
          continue;
        }

        const validation = await validateTransaction(
          signedTxn,
          purchase.productId,
          purchase.transactionId
        );
        if (validation.success) {
          granted = true;
          markPurchasePending(purchase, false);
          try {
            await finishTransaction({
              purchase,
              isConsumable: purchase.productId?.includes?.('.credits.'),
            });
          } catch (finishErr) {
            console.warn('[useIosIap] Restore finish skipped:', finishErr.message);
          }
        } else {
          lastError = validation.error || 'Purchase could not be verified. Please try Restore again.';
          pending ||= validation.pending !== false;
          if (validation.pending !== false) markPurchasePending(purchase, true);
          console.warn('[useIosIap] Restore skipped a transaction:', purchase.productId, lastError);
        }
      }

      if (lastError) return { success: false, pending, restoredSome: granted, error: lastError };
      if (granted) return { success: true };
      return { success: true, message: 'No purchases to restore' };
    } catch (err) {
      console.error('[useIosIap] Restore error:', err);
      setError(err.message || 'Failed to restore purchases');
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [connect, validateTransaction, markPurchasePending]);

  // Keep refs to the latest accessToken and validateTransaction so the
  // purchase listener (registered once) always uses current values without
  // needing to re-subscribe on every token change.
  const accessTokenRef = useRef(accessToken);
  const validateTransactionRef = useRef(validateTransaction);
  useEffect(() => { accessTokenRef.current = accessToken; }, [accessToken]);
  useEffect(() => { validateTransactionRef.current = validateTransaction; }, [validateTransaction]);

  // Set up purchase listeners.
  // The listener:
  //   1. Passes raw purchase data to a waiting purchaseProduct/purchaseCreditPack
  //      call (which handles validation itself).
  //   2. For non-user-initiated transactions (auto-renewals, plan changes,
  //      credit packs delivered while app was backgrounded), validates with the
  //      backend before finishing. For credit packs this is critical — it's the
  //      only path that grants credits if the user-initiated flow didn't complete.
  useEffect(() => {
    if (!isNativePlatform) return;

    console.log('[useIosIap] Setting up purchase listeners');

    try {
    purchaseUpdateSubscription.current = purchaseUpdatedListener(async (purchase) => {
      console.log('[useIosIap] === PURCHASE LISTENER FIRED ===');
      console.log('[useIosIap] productId:', purchase.productId);
      console.log('[useIosIap] transactionId:', purchase.transactionId);
      console.log('[useIosIap] hasPromiseWaiting:', !!purchasePromiseRef.current);

      // Unrelated recovered purchases must not resolve a new product's checkout.
      if (purchasePromiseRef.current?.productId === purchase.productId) {
        clearTimeout(purchaseFailsafeTimer.current);
        purchaseFailsafeTimer.current = null;
        console.log('[useIosIap] Passing purchase to user-initiated handler');
        purchasePromiseRef.current.resolve(purchase);
        // Keep subsequent events in this checkout until the store request
        // resolves, rather than validating the same purchase concurrently.
        return;
      }

      if (Platform.OS === 'android' && purchase.purchaseStateAndroid === 2) return;

      // Non-user-initiated transaction (auto-renewal, subscription group
      // upgrade/downgrade, credit pack delivered while app was backgrounded, etc.).
      // Validate ALL transaction types before finishing. For credit packs this is
      // the only path that grants credits — skipping it loses the user's money.
      const isCreditPack = purchase.productId?.includes?.('.credits.');
      const signedTxn = isApplePlatform
        ? (purchase.jwsRepresentationIos || purchase.transactionReceipt)
        : purchase.purchaseToken;

      if (!signedTxn || !accessTokenRef.current) {
        console.warn('[useIosIap] Cannot validate non-user-initiated transaction (no JWS or auth) — leaving unfinished:', purchase.productId);
        return;
      }

      console.log('[useIosIap] Validating non-user-initiated transaction:', purchase.productId);
      try {
        const result = await validateTransactionRef.current(signedTxn, purchase.productId, purchase.transactionId);
        if (!result.success) {
          if (result.pending) markPurchasePending(purchase, true);
          console.warn('[useIosIap] Non-user-initiated validation failed — leaving unfinished:', purchase.productId);
          return;
        }
      } catch (valErr) {
        console.warn('[useIosIap] Non-user-initiated validation threw — leaving unfinished:', valErr.message);
        return;
      }

      markPurchasePending(purchase, false);
      try {
        await finishTransaction({ purchase, isConsumable: isCreditPack });
        console.log('[useIosIap] Finished non-user-initiated transaction:', purchase.productId);
      } catch (err) {
        console.warn('[useIosIap] Error finishing non-user-initiated transaction:', err);
      }
    });

    purchaseErrorSubscription.current = purchaseErrorListener((error) => {
      console.error('[useIosIap] Purchase error listener:', error?.code, error?.message);

      if (purchaseFailsafeTimer.current) {
        clearTimeout(purchaseFailsafeTimer.current);
        purchaseFailsafeTimer.current = null;
      }

      const wasCancelled = isUserCancelledPurchaseError(error);

      // If a user-initiated purchase is waiting, signal the error/cancel.
      if (purchasePromiseRef.current) {
        purchasePromiseRef.current.resolve(
          wasCancelled
            ? { _cancelled: true }
            : { _error: error.message || 'Purchase failed' }
        );
        purchasePromiseRef.current = null;
        return;
      }

      if (!wasCancelled) {
        setError(error.message || 'Purchase failed. Please try again.');
      }
      setPurchaseInProgress(false);
    });
    } catch (e) {
      console.warn('[useIosIap] Failed to set up purchase listeners (IAP unavailable):', e.message);
      setError(null);
    }

    return () => {
      if (purchaseUpdateSubscription.current) {
        purchaseUpdateSubscription.current.remove();
        purchaseUpdateSubscription.current = null;
      }
      if (purchaseErrorSubscription.current) {
        purchaseErrorSubscription.current.remove();
        purchaseErrorSubscription.current = null;
      }
      if (purchaseFailsafeTimer.current) {
        clearTimeout(purchaseFailsafeTimer.current);
        purchaseFailsafeTimer.current = null;
      }
    };
  }, [markPurchasePending, setPurchaseInProgress]);

  // Connect to the store on mount
  useEffect(() => {
    if (isNativePlatform) {
      connect();
    }

    return () => {
      if (isNativePlatform) {
        disconnect();
      }
    };
  }, [connect, disconnect]);

  // Fetch products once connected — separate effect avoids the stale-closure
  // bug where fetchProducts captured isConnected=false from the initial render.
  useEffect(() => {
    if (isConnected) {
      console.log('[useIosIap] Connected to store, fetching products...');
      fetchProducts();
    }
  }, [isConnected, fetchProducts]);

  // Auth hydration can finish after the StoreKit connection. Process pending
  // transactions only when both are ready, and retry automatically if the
  // signed-in account changes without requiring another app launch.
  useEffect(() => {
    if (!isNativePlatform || !isConnected || !accessToken) return;
    processPendingIosTransactions().catch((err) => {
      console.warn('[useIosIap] Background pending-txn processing failed:', err.message);
    });
  }, [isConnected, accessToken, processPendingIosTransactions]);

  // Get product by ID (subscriptions or credit pack consumables)
  const getProduct = useCallback((productId) => {
    const sub = products.find((p) => p.productId === productId);
    if (sub) return sub;
    return creditPackProducts.find((p) => p.productId === productId) || null;
  }, [products, creditPackProducts]);

  // Get formatted price
  const getFormattedPrice = useCallback((productId) => {
    const product = getProduct(productId);
    if (!product) return null;
    return product.productId?.includes('.credits.')
      ? product.localizedPrice || product.oneTimePurchaseOfferDetails?.formattedPrice || null
      : getSubscriptionPrice(product, Platform.OS);
  }, [getProduct]);

  return {
    isConnected,
    isLoading,
    products,
    creditPackProducts,
    error,
    purchaseInProgress,
    PRODUCT_IDS,
    getCreditPackIapProductId,
    connect,
    disconnect,
    fetchProducts,
    purchaseProduct,
    purchaseCreditPack,
    restorePurchases,
    getProduct,
    getFormattedPrice,
    purchasePending,
    clearError: () => setError(null),
  };
}
